import { useEffect, useRef, useMemo } from 'react';
import { useAgentChatPanelContext } from 'App/components/AgentChatPanelContext';
import { useAiGeneratedValues } from 'commons/hooks';
import { AI_FIELD_NAVIGATION_ATTRIBUTES } from './constants';
import { createControllerInstance } from './handlers';
import { UseAiFieldOptions } from './types';

/**
 * Registers a component with the AgentChatActionBus, enabling AI-driven interactions.
 * Use this hook to enable AI operations (replace, add, remove, clear) on a component.
 *
 * The first generic type defines the value type managed by the handlers (AI backend communication),
 * and the second one defines the value type managed by the AI component states (after mappings,
 * transformations, etc.). By default, the second type is the same as the first one, but it can be overridden.
 *
 * @example
 * type BackendFlowType = {
 *   value_before: number;
 * }
 * type ComponentFlowType = {
 *   valueAfter: string;
 * }
 *
 * const { navigationAttributes } = useAiField<BackendFlowType, ComponentFlowType>({
 *   componentId: 'product.prices',
 *   getValue: () => {
 *     return {
 *       value_before: 100,
 *     };
 *   },
 *   onReplace: (value) => {
 *     const componentValues = mapBackendValueToComponentValue(value);
 *     return {
 *       aiGeneratedValues: componentValues,
 *     };
 *   },
 * });
 *
 */
function useAiField<T = unknown, V = T>(options: UseAiFieldOptions<T, V>) {
  const { componentId, getValue, onReplace, onAdd, onRemove, onClear } =
    options;

  const { actionBus: aiActionBus } = useAgentChatPanelContext();
  const { setAiGeneratedValues } = useAiGeneratedValues<T | V | null>(
    componentId,
  );

  /**
   * For now, we just spread data attributes for navigation. These objects can be easily passed along component props, unlike Refs that they require 'forwardRef' to work. If we migrate to React 19, we can improve this.
   */
  const navigationAttributes = useMemo(
    () => ({ [AI_FIELD_NAVIGATION_ATTRIBUTES]: componentId }),
    [componentId],
  );

  /**
   * Store handlers in refs to prevent re-registration on every render.
   * Refs ensure stable references while always invoking the latest closure.
   */
  const getValueRef = useRef<(() => T | undefined) | undefined>(getValue);
  const onReplaceRef = useRef(onReplace);
  const onAddRef = useRef(onAdd);
  const onRemoveRef = useRef(onRemove);
  const onClearRef = useRef(onClear);

  getValueRef.current = getValue;
  onReplaceRef.current = onReplace;
  onAddRef.current = onAdd;
  onRemoveRef.current = onRemove;
  onClearRef.current = onClear;

  useEffect(() => {
    if (!aiActionBus) return;

    const controllerInstance = createControllerInstance<T, V>(
      {
        getValue: getValueRef,
        onReplace: onReplaceRef,
        onAdd: onAddRef,
        onRemove: onRemoveRef,
        onClear: onClearRef,
      },
      componentId,
      setAiGeneratedValues,
    );

    aiActionBus.register<T, V>(componentId, controllerInstance);
    return () => {
      aiActionBus.unregister(componentId);
    };
  }, [componentId, setAiGeneratedValues, aiActionBus]);

  return {
    navigationAttributes,
  } as const;
}

export { useAiField };
export default useAiField;
